'use client';

import {
  useRef, useEffect, useCallback, useImperativeHandle, forwardRef,
} from 'react';

/**
 * Interactive SVG overlay for visual editing.
 * Renders the SVG and allows selecting, dragging, and deleting elements.
 * Uses ref-based innerHTML to prevent React re-renders from resetting DOM edits.
 */
function SvghmiEditorOverlay({
  svgHtml,
  selectedId,
  onSelect,
  onMove,
  onDelete,
  lockedIds,
}, ref) {
  const containerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    removeElement(id) {
      const container = containerRef.current;
      if (!container) return;
      const el = container.querySelector(`[id="${id}"]`);
      if (el) el.remove();
      container.querySelectorAll('.svghmi-editor-highlight').forEach((h) => h.remove());
    },
  }));
  const dragState = useRef(null);
  const renderedHtmlRef = useRef(null);

  // Set innerHTML only when svgHtml actually changes (not on every re-render)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (svgHtml !== renderedHtmlRef.current) {
      container.innerHTML = svgHtml || '';
      renderedHtmlRef.current = svgHtml;

      // Apply cursor styles after setting new HTML
      const svgRoot = container.querySelector('svg');
      if (svgRoot) {
        svgRoot.querySelectorAll('[id]').forEach((el) => {
          if (el.id === svgRoot.id) return;
          el.style.cursor = lockedIds.has(el.id) ? 'not-allowed' : 'move';
        });
      }
    }
  }, [svgHtml, lockedIds]);

  // Find the nearest ancestor with an id attribute
  const findSelectableElement = useCallback((target) => {
    let el = target;
    const container = containerRef.current;
    if (!container) return null;
    const svgRoot = container.querySelector('svg');
    while (el && el !== svgRoot && el !== container) {
      if (el.id && el.id !== svgRoot?.id) return el;
      el = el.parentElement;
    }
    return null;
  }, []);

  // Convert screen coordinates to SVG coordinates
  const screenToSvg = useCallback((svgRoot, screenX, screenY) => {
    const ctm = svgRoot.getScreenCTM();
    if (!ctm) return { x: screenX, y: screenY };
    const inv = ctm.inverse();
    return {
      x: inv.a * screenX + inv.c * screenY + inv.e,
      y: inv.b * screenX + inv.d * screenY + inv.f,
    };
  }, []);

  // Apply visual selection highlight
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Remove old highlights
    container.querySelectorAll('.svghmi-editor-highlight').forEach((el) => el.remove());

    if (!selectedId) return;

    const svgRoot = container.querySelector('svg');
    const el = container.querySelector(`[id="${selectedId}"]`);
    if (!el || !svgRoot) return;

    try {
      const bbox = el.getBBox();
      const ctm = el.getCTM();
      const svgCtm = svgRoot.getCTM();
      if (!ctm || !svgCtm) return;

      const rel = svgCtm.inverse().multiply(ctm);
      const pt = svgRoot.createSVGPoint();

      const corners = [
        { x: bbox.x, y: bbox.y },
        { x: bbox.x + bbox.width, y: bbox.y },
        { x: bbox.x + bbox.width, y: bbox.y + bbox.height },
        { x: bbox.x, y: bbox.y + bbox.height },
      ].map((c) => {
        pt.x = c.x;
        pt.y = c.y;
        const tp = pt.matrixTransform(rel);
        return { x: tp.x, y: tp.y };
      });

      const minX = Math.min(...corners.map((c) => c.x));
      const minY = Math.min(...corners.map((c) => c.y));
      const maxX = Math.max(...corners.map((c) => c.x));
      const maxY = Math.max(...corners.map((c) => c.y));

      const isLocked = lockedIds.has(selectedId);
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', minX - 2);
      rect.setAttribute('y', minY - 2);
      rect.setAttribute('width', maxX - minX + 4);
      rect.setAttribute('height', maxY - minY + 4);
      rect.setAttribute('fill', 'none');
      rect.setAttribute('stroke', isLocked ? '#ef4444' : '#3b82f6');
      rect.setAttribute('stroke-width', '2');
      rect.setAttribute('stroke-dasharray', isLocked ? '4 2' : 'none');
      rect.setAttribute('pointer-events', 'none');
      rect.classList.add('svghmi-editor-highlight');
      svgRoot.appendChild(rect);
    } catch { /* getBBox may fail for hidden elements */ }
  }, [selectedId, lockedIds]);

  // Pointer down: select element and start drag
  const handlePointerDown = useCallback((e) => {
    const container = containerRef.current;
    if (!container) return;

    const target = findSelectableElement(e.target);
    if (!target) {
      onSelect(null);
      return;
    }

    onSelect(target.id);

    if (lockedIds.has(target.id)) return;

    const svgRoot = container.querySelector('svg');
    if (!svgRoot) return;

    const start = screenToSvg(svgRoot, e.clientX, e.clientY);
    dragState.current = {
      id: target.id,
      lastX: start.x,
      lastY: start.y,
      totalDx: 0,
      totalDy: 0,
      moved: false,
    };

    e.preventDefault();
  }, [findSelectableElement, onSelect, lockedIds, screenToSvg]);

  // Pointer move: drag element visually
  const handlePointerMove = useCallback((e) => {
    if (!dragState.current) return;
    const container = containerRef.current;
    if (!container) return;

    const svgRoot = container.querySelector('svg');
    if (!svgRoot) return;

    const current = screenToSvg(svgRoot, e.clientX, e.clientY);
    const dx = current.x - dragState.current.lastX;
    const dy = current.y - dragState.current.lastY;

    if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
      dragState.current.moved = true;
      dragState.current.totalDx += dx;
      dragState.current.totalDy += dy;
      dragState.current.lastX = current.x;
      dragState.current.lastY = current.y;

      // Optimistic visual update — modify rendered SVG DOM directly
      const el = container.querySelector(`[id="${dragState.current.id}"]`);
      if (el) {
        const tag = el.tagName.toLowerCase();
        if (tag === 'circle' || tag === 'ellipse') {
          el.setAttribute('cx', parseFloat(el.getAttribute('cx') || 0) + dx);
          el.setAttribute('cy', parseFloat(el.getAttribute('cy') || 0) + dy);
        } else if (tag === 'line') {
          el.setAttribute('x1', parseFloat(el.getAttribute('x1') || 0) + dx);
          el.setAttribute('y1', parseFloat(el.getAttribute('y1') || 0) + dy);
          el.setAttribute('x2', parseFloat(el.getAttribute('x2') || 0) + dx);
          el.setAttribute('y2', parseFloat(el.getAttribute('y2') || 0) + dy);
        } else if (['rect', 'text', 'image', 'foreignObject', 'use'].includes(tag)) {
          el.setAttribute('x', parseFloat(el.getAttribute('x') || 0) + dx);
          el.setAttribute('y', parseFloat(el.getAttribute('y') || 0) + dy);
        } else {
          const existing = el.getAttribute('transform') || '';
          const m = existing.match(/translate\(\s*([-\d.]+)\s*[,\s]\s*([-\d.]+)\s*\)/);
          if (m) {
            el.setAttribute('transform', existing.replace(
              /translate\([^)]*\)/,
              `translate(${parseFloat(m[1]) + dx}, ${parseFloat(m[2]) + dy})`,
            ));
          } else {
            el.setAttribute('transform', `translate(${dx}, ${dy}) ${existing}`.trim());
          }
        }
      }
    }
  }, [screenToSvg]);

  // Pointer up: commit move to source XML
  const handlePointerUp = useCallback(() => {
    if (!dragState.current) return;
    const {
      id, totalDx, totalDy, moved,
    } = dragState.current;
    dragState.current = null;
    if (moved && (Math.abs(totalDx) > 0.5 || Math.abs(totalDy) > 0.5)) {
      onMove(id, totalDx, totalDy);
    }
  }, [onMove]);

  // Keyboard handler: arrow keys to move, Delete/Backspace to delete
  const handleKeyDown = useCallback((e) => {
    if (!selectedId) return;

    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      // Remove element from rendered DOM immediately
      const container = containerRef.current;
      if (container) {
        const el = container.querySelector(`[id="${selectedId}"]`);
        if (el) el.remove();
        // Remove highlight too
        container.querySelectorAll('.svghmi-editor-highlight').forEach((h) => h.remove());
      }
      onDelete(selectedId);
      return;
    }

    if (lockedIds.has(selectedId)) return;

    const step = e.shiftKey ? 10 : 1;
    let dx = 0;
    let dy = 0;

    switch (e.key) {
    case 'ArrowLeft': dx = -step; break;
    case 'ArrowRight': dx = step; break;
    case 'ArrowUp': dy = -step; break;
    case 'ArrowDown': dy = step; break;
    default: return;
    }

    e.preventDefault();

    // Apply visual update for arrow keys too
    const container = containerRef.current;
    if (container) {
      const el = container.querySelector(`[id="${selectedId}"]`);
      if (el) {
        const tag = el.tagName.toLowerCase();
        if (tag === 'circle' || tag === 'ellipse') {
          el.setAttribute('cx', parseFloat(el.getAttribute('cx') || 0) + dx);
          el.setAttribute('cy', parseFloat(el.getAttribute('cy') || 0) + dy);
        } else if (tag === 'line') {
          el.setAttribute('x1', parseFloat(el.getAttribute('x1') || 0) + dx);
          el.setAttribute('y1', parseFloat(el.getAttribute('y1') || 0) + dy);
          el.setAttribute('x2', parseFloat(el.getAttribute('x2') || 0) + dx);
          el.setAttribute('y2', parseFloat(el.getAttribute('y2') || 0) + dy);
        } else if (['rect', 'text', 'image', 'foreignObject', 'use'].includes(tag)) {
          el.setAttribute('x', parseFloat(el.getAttribute('x') || 0) + dx);
          el.setAttribute('y', parseFloat(el.getAttribute('y') || 0) + dy);
        } else {
          const existing = el.getAttribute('transform') || '';
          const m = existing.match(/translate\(\s*([-\d.]+)\s*[,\s]\s*([-\d.]+)\s*\)/);
          if (m) {
            el.setAttribute('transform', existing.replace(
              /translate\([^)]*\)/,
              `translate(${parseFloat(m[1]) + dx}, ${parseFloat(m[2]) + dy})`,
            ));
          } else {
            el.setAttribute('transform', `translate(${dx}, ${dy}) ${existing}`.trim());
          }
        }
      }
    }

    onMove(selectedId, dx, dy);
  }, [selectedId, onMove, onDelete, lockedIds]);

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onKeyDown={handleKeyDown}
      className="svghmi-preview-wrapper w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ outline: 'none', touchAction: 'none' }}
    />
  );
}

export default forwardRef(SvghmiEditorOverlay);
